import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Download, Filter, Search } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { TimesheetRow } from '@/components/timesheets/TimesheetRow';

const AdminTimesheets = ({
 auth, onLogout }) => {
  const [entries, setEntries] = useState([]);
  const [employees, setEmployees] = useState({});
  const [employeesList, setEmployeesList] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(true);

  const loadEmployees = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/employees`);
      const empMap = {};
      const empList = [];
      res.data.forEach((emp) => {
        empMap[emp.id] = `${emp.first_name} ${emp.last_name}`;
        if (emp.is_active) empList.push({ id: emp.id, name: `${emp.first_name} ${emp.last_name}` });
      });
      setEmployees(empMap);
      setEmployeesList(empList);
    } catch (err) { console.error(err); }
  }, []);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (selectedEmployee !== 'all') params.employee_id = selectedEmployee;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await axios.get(`${API}/time/all`, { params });
      setEntries(res.data);
    } catch (err) { console.error(err); toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, [selectedEmployee, startDate, endDate]);

  useEffect(() => { loadEmployees(); }, [loadEmployees]);
  useEffect(() => { loadEntries(); }, [loadEntries]);

  const handleExport = async (format) => {
    try {
      const url = selectedEmployee && selectedEmployee !== 'all'
        ? `${API}/export/${format}?employee_id=${selectedEmployee}`
        : `${API}/export/${format}`;
      
      const employeeName = selectedEmployee && selectedEmployee !== 'all'
        ? employeesList.find(e => e.id === selectedEmployee)?.name.replace(' ', '_') || 'employe'
        : 'tous';
      
      const filename = `rapport_complet_${employeeName}_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      const { downloadPdf } = await import('@/utils/api');
      await downloadPdf(url, filename);

      toast.success('Export réussi');
    } catch (error) {
      console.error('Erreur export:', error);
      toast.error('Erreur lors de l\'export');
    }
  };

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 md:p-8 space-y-6">
        <div className="flex flex-col space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-heading font-bold text-slate-900 tracking-tight" data-testid="timesheets-title">
                Pointages
              </h1>
              <p className="text-base text-slate-600 mt-1">{entries.length} enregistrement(s)</p>
            </div>
          </div>

          {/* Filters & Export */}
          <Card className="p-6 rounded-xl border border-slate-200 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1 block">Employé</Label>
                <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                  <SelectTrigger data-testid="employee-filter-select" className="w-full">
                    <SelectValue placeholder="Tous" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les employés</SelectItem>
                    {employeesList.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1 block">Date début</Label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="start-date-filter" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1 block">Date fin</Label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="end-date-filter" />
              </div>
              <div className="flex space-x-2">
                <Button onClick={() => handleExport('excel')} className="btn-success rounded-md flex-1" data-testid="export-excel-button">
                  <Download className="mr-1 w-4 h-4" /> Excel
                </Button>
                <Button onClick={() => handleExport('pdf')} className="btn-danger rounded-md flex-1" data-testid="export-pdf-button">
                  <Download className="mr-1 w-4 h-4" /> PDF
                </Button>
              </div>
            </div>
            {(selectedEmployee !== 'all' || startDate || endDate) && (
              <div className="mt-3 text-sm text-slate-600 flex items-center gap-2">
                <Filter className="w-3 h-3" />
                Filtres actifs :
                {selectedEmployee !== 'all' && <span className="font-medium">{employees[selectedEmployee]}</span>}
                {startDate && <span>du {startDate}</span>}
                {endDate && <span>au {endDate}</span>}
                <button onClick={() => { setSelectedEmployee('all'); setStartDate(''); setEndDate(''); }} className="text-indigo-600 hover:underline ml-2">
                  Réinitialiser
                </button>
              </div>
            )}
          </Card>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-500">Chargement...</p>
          </div>
        ) : entries.length === 0 ? (
          <Card className="p-12 text-center rounded-xl">
            <p className="text-slate-500">Aucun pointage trouvé</p>
          </Card>
        ) : (
          <Card className="p-6 rounded-xl border border-slate-200 shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="timesheets-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    {['', 'Employé', 'Date', 'Arrivée', 'Sortie midi', 'Retour', 'Départ', 'Pauses déduites', 'Total (h)', 'Statut'].map((h, i) => (
                      <th key={i} className="text-left py-3 px-3 text-xs uppercase tracking-wider text-slate-500 font-medium whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <TimesheetRow key={entry.id || `${entry.employee_id}-${entry.date}`} entry={entry} name={employees[entry.employee_id] || (entry.employee_id || '').substring(0, 8)} />
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-slate-500 mt-3">Cliquez sur une ligne pour voir le détail. Pause du matin (20 min) et pause de midi (1h30 si non timbrée) déduites automatiquement ; si midi est timbré, le temps réel est déduit.</p>
            </div>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminTimesheets;